import { useMemo, useState } from 'react'
import { Tabs } from './ui/Tabs'
import { formatDay, formatDayShort, groupByLocalDay, isToday } from '../time'

/**
 * A tournament's days, one at a time.
 *
 * **Why tabs rather than the scroll.** The International runs a fortnight, and every played series
 * was stacked into one continuous column — so reaching the current day meant scrolling past every
 * day before it, and the page grew for the whole event. The days are genuinely separate things a
 * reader moves between, which is what a tab set is for.
 *
 * The strip itself is the shared {@link Tabs} control, so the ARIA roles, the roving arrow-key
 * focus and the scroll-into-view all come from one place rather than being re-derived here.
 */
export function DayTabs<T extends { startTime: number }>({
  items,
  render,
  emptyLabel = 'Nothing on this day.',
}: {
  items: readonly T[]
  render: (dayItems: T[]) => React.ReactNode
  emptyLabel?: string
}) {
  // Newest first: the interesting day is nearly always the most recent one.
  const days = useMemo(() => groupByLocalDay(items).reverse(), [items])

  const [selected, setSelected] = useState<string | null>(null)

  // Today while the tournament is running, the latest day once it is over — which is what a reader
  // opening the page mid-event and after it respectively wants. Recomputed rather than stored, so a
  // day appearing while the page is open cannot leave the selection pointing at nothing.
  const active = useMemo(() => {
    if (selected && days.some((d) => d.key === selected)) return selected
    return days.find((d) => isToday(d.startTime))?.key ?? days[0]?.key ?? ''
  }, [days, selected])

  if (days.length === 0) {
    return null
  }

  const day = days.find((d) => d.key === active) ?? days[0]

  return (
    <section>
      <Tabs
        className="mb-4"
        ariaLabel="Match days"
        variant="segmented"
        active={day.key}
        onChange={setSelected}
        tabs={days.map((d) => ({
          key: d.key,
          label: isToday(d.startTime) ? 'Today' : formatDayShort(d.startTime),
          badge: d.items.length,
        }))}
      />

      <h3 className="mb-3 text-sm font-medium text-ink-soft">{formatDay(day.startTime)}</h3>
      {day.items.length === 0 ? (
        <p className="text-sm text-ink-faint">{emptyLabel}</p>
      ) : (
        render(day.items)
      )}
    </section>
  )
}
